import { useState } from 'react';
import {
  App as AntApp,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'antd';
import { InboxOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useCreateCustomer, useUpdateCustomer, type Customer } from '../api/customers';
import { api, asApiError } from '@/shared/lib/api';

const SOURCES = ['walk_in', 'referral', 'social', 'ads', 'partner', 'website', 'other'];
const ACCEPT_MIME = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_FILE_BYTES = 10 * 1024 * 1024;

type CustomerFormModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  /** Required in `edit` mode — prefills the form. */
  customer?: Customer;
  onClose: () => void;
  /** Called after a successful create, with the new customer. */
  onCreated?: (customer: Customer) => void;
};

/**
 * Single create/edit form for customers. Both modes render the exact same
 * fields; the only differences are the prefilled values, the title/buttons,
 * and the mutation that fires on submit.
 */
export function CustomerFormModal({ open, mode, customer, onClose, onCreated }: CustomerFormModalProps) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const [form] = Form.useForm();
  const [passportFile, setPassportFile] = useState<UploadFile | null>(null);

  const isEdit = mode === 'edit';
  const pending = isEdit ? update.isPending : create.isPending;

  function handleClose() {
    form.resetFields();
    setPassportFile(null);
    onClose();
  }

  async function onOk() {
    try {
      const values = await form.validateFields();
      const passportFileUrl = passportFile?.response?.data?.key as string | undefined;

      if (isEdit && customer) {
        // pinfl is shown masked (e.g. 12345*****6789). Only send a freshly typed
        // value; otherwise omit it so the stored value is preserved.
        const pinflVal = values.pinfl as string | undefined;
        const pinflToSend = pinflVal && !pinflVal.includes('*') ? pinflVal : undefined;
        await update.mutateAsync({
          id: customer.id,
          input: {
            firstName: values.firstName,
            lastName: values.lastName,
            middleName: values.middleName,
            gender: values.gender ?? null,
            additionalPhones: values.additionalPhones ?? [],
            email: values.email || null,
            address: values.address,
            registrationAddress: values.registrationAddress || null,
            dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : undefined,
            passportSeries: values.passportSeries ? String(values.passportSeries).toUpperCase() : null,
            passportNumber: values.passportNumber || null,
            passportIssueDate: values.passportIssueDate ? values.passportIssueDate.format('YYYY-MM-DD') : null,
            passportExpiryDate: values.passportExpiryDate ? values.passportExpiryDate.format('YYYY-MM-DD') : null,
            passportIssuedBy: values.passportIssuedBy,
            passportFileUrl: passportFileUrl ?? undefined,
            pinfl: pinflToSend,
            source: values.source,
            notes: values.notes || null,
            preferredLanguage: values.preferredLanguage,
          },
        });
        void message.success(t('customers.updated'));
        handleClose();
      } else {
        const created = await create.mutateAsync({
          ...values,
          dateOfBirth: values.dateOfBirth?.format('YYYY-MM-DD'),
          passportIssueDate: values.passportIssueDate?.format('YYYY-MM-DD'),
          passportExpiryDate: values.passportExpiryDate?.format('YYYY-MM-DD'),
          passportSeries: values.passportSeries?.toUpperCase(),
          passportFileUrl,
        });
        void message.success(t('customers.created'));
        handleClose();
        onCreated?.(created);
      }
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: ACCEPT_MIME,
    fileList: passportFile ? [passportFile] : [],
    onChange(info) {
      const last = info.fileList[info.fileList.length - 1];
      setPassportFile(last ?? null);
      if (info.file.status === 'done') void message.success(t('customers.passportFileSaved'));
      if (info.file.status === 'error') void message.error(t('customers.passportFileFailed'));
    },
    onRemove() {
      setPassportFile(null);
    },
    beforeUpload(file) {
      if (file.size > MAX_FILE_BYTES) {
        void message.error(t('customers.passportFileTooLarge'));
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    async customRequest({ file, onSuccess, onError, onProgress }) {
      try {
        const fd = new FormData();
        fd.append('file', file as Blob);
        const res = await api.post('/uploads/file', fd, {
          params: { purpose: 'customers/passport' },
          onUploadProgress: (e) => {
            if (e.total) onProgress?.({ percent: Math.round((e.loaded / e.total) * 100) });
          },
        });
        onSuccess?.(res.data);
      } catch (err) {
        onError?.(axios.isAxiosError(err) ? new Error(asApiError(err).message) : (err as Error));
      }
    },
  };

  const initialValues =
    isEdit && customer
      ? {
          firstName: customer.firstName,
          lastName: customer.lastName,
          middleName: customer.middleName ?? undefined,
          gender: customer.gender ?? undefined,
          additionalPhones: customer.additionalPhones ?? [],
          phone: customer.phone,
          email: customer.email ?? undefined,
          address: customer.address ?? undefined,
          registrationAddress: customer.registrationAddress ?? undefined,
          dateOfBirth: customer.dateOfBirth ? dayjs(customer.dateOfBirth) : undefined,
          passportSeries: customer.passportSeries ?? undefined,
          passportNumber: customer.passportNumber ?? undefined,
          passportIssueDate: customer.passportIssueDate ? dayjs(customer.passportIssueDate) : undefined,
          passportExpiryDate: customer.passportExpiryDate ? dayjs(customer.passportExpiryDate) : undefined,
          passportIssuedBy: customer.passportIssuedBy ?? undefined,
          pinfl: customer.pinfl ?? undefined,
          source: customer.source,
          notes: customer.notes ?? undefined,
          preferredLanguage: customer.preferredLanguage,
        }
      : { source: 'walk_in', preferredLanguage: 'ru' };

  return (
    <Modal
      title={isEdit ? t('customers.editTitle') : t('customers.newTitle')}
      open={open}
      onCancel={handleClose}
      onOk={onOk}
      confirmLoading={pending}
      okText={isEdit ? t('common.save') : t('common.create')}
      cancelText={t('common.cancel')}
      width={840}
      style={{ top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: 8 } }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initialValues}>
        <SectionTitle step={1} title={t('customers.sectionIdentity')} />
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name="lastName" label={t('customers.lastName')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="firstName" label={t('customers.firstName')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="middleName" label={t('customers.middleName')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name="dateOfBirth" label={t('customers.birthDate')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder={t('customers.birthDatePlaceholder')} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="gender" label={t('customers.gender')}>
              <Select
                allowClear
                options={[
                  { value: 'male', label: t('customers.genderMale') },
                  { value: 'female', label: t('customers.genderFemale') },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="preferredLanguage" label={t('customers.preferredLanguage')}>
              <Select
                options={[
                  { value: 'ru', label: 'Русский' },
                  { value: 'uz', label: "O'zbek" },
                  { value: 'en', label: 'English' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle step={2} title={t('customers.sectionContact')} />
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="phone" label={t('customers.phone')} rules={[{ required: true }]}>
              <Input placeholder="+998 90 123 45 67" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label={t('customers.email')} rules={[{ type: 'email' }]}>
              <Input placeholder="example@domain.uz" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label={t('customers.additionalPhones')}>
              <Form.List name="additionalPhones">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {fields.map(({ key, name, ...rest }) => (
                      <Space key={key} align="baseline">
                        <Form.Item
                          {...rest}
                          name={name}
                          rules={[{ required: true, message: t('customers.phone') }]}
                          noStyle
                        >
                          <Input placeholder="+998 90 123 45 67" style={{ width: 260 }} />
                        </Form.Item>
                        <MinusCircleOutlined style={{ color: '#94a3b8' }} onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                      {t('customers.addPhone')}
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="address" label={t('customers.address')} rules={[{ required: true }]}>
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="registrationAddress" label={t('customers.registrationAddress')}>
              <Input maxLength={255} />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle step={3} title={t('customers.sectionPassport')} />
        <Row gutter={12}>
          <Col xs={12} md={6}>
            <Form.Item
              name="passportSeries"
              label={t('customers.passportSeries')}
              rules={[
                { required: true, message: t('customers.passportSeriesRequired') },
                { pattern: /^[A-Za-z]{2}$/, message: t('customers.passportSeriesHelp') },
              ]}
            >
              <Input maxLength={2} placeholder="AA" style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="passportNumber"
              label={t('customers.passportNumber')}
              rules={[
                { required: true, message: t('customers.passportNumberRequired') },
                { pattern: /^\d{7}$/, message: t('customers.passportNumberHelp') },
              ]}
            >
              <Input maxLength={7} placeholder="1234567" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="passportIssueDate"
              label={t('customers.passportIssueDate')}
              rules={[{ required: true, message: t('customers.passportIssueDateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="passportExpiryDate"
              label={t('customers.passportExpiryDate')}
              rules={[{ required: true, message: t('customers.passportExpiryDateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="passportIssuedBy" label={t('customers.passportIssuedBy')} rules={[{ required: true }]}>
          <Input maxLength={120} />
        </Form.Item>

        <Form.Item label={t('customers.uploadPassport')} extra={t('customers.uploadPassportHint')}>
          <Upload.Dragger {...uploadProps} style={{ padding: 12 }}>
            <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
              <InboxOutlined style={{ fontSize: 28, color: '#16a34a' }} />
            </p>
            <p style={{ fontSize: 13, margin: 0 }}>{t('customers.uploadPassportDrop')}</p>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>
              {t('customers.uploadPassportFormats')}
            </p>
          </Upload.Dragger>
        </Form.Item>

        <SectionTitle step={4} title={t('customers.sectionStateIds')} />
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="pinfl" label={t('customers.pinfl')} extra={t('customers.pinflHelp')} rules={[{ required: true }, { len: 14, message: t('customers.pinflLen') }]}>
              <Input maxLength={14} />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle step={5} title={t('customers.sectionCrm')} />
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="source" label={t('customers.source_')}>
              <Select options={SOURCES.map((s) => ({ value: s, label: t(`customers.source.${s}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="notes" label={t('customers.notes')}>
              <Input.TextArea rows={1} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function SectionTitle({ step, title }: { step: number; title: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 12px' }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 4,
          background: '#f1f5f9',
          color: '#475569',
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {step}
      </span>
      <Typography.Text
        strong
        style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        {title}
      </Typography.Text>
    </div>
  );
}
